import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { defineAction } from '~/prix'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import citizenHealthProfileStatsAction from './citizenHealthProfileStats.action.json' with { type: 'json' }
import { z } from 'zod'
import { enumerated } from '~/prix/types/enumerated'
import expression from '~/prix/types/expression'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { citizenLocationShape, citizenShapeBase } from '../../citizen/stats/citizenShape'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'
import { defaultDetectionLayers } from '~/utils/server/stats/defaultTimeSeriesLayers'

export const citizenHealthProfileShape = z.object({
  fathersName: z.string().describe('Nome do pai').nullish(),
  mothersName: z.string().describe('Nome da mãe').nullish(),
  email: z.string().describe('E-mail').nullish(),
  skinColorOrRace: enumerated({
    white: 'Branco',
    black: 'Negro',
    mixedRace: 'Pardo',
    indigenous: 'Indígena',
    yellow: 'Amarelo',
    noInformation: 'Sem informação',
  })
    .describe('Cor de pele/Raça')
    .nullish(),
  ethnicity: z.string().describe('Etnia').nullish(),
  nationality: enumerated({
    brazilian: 'Brasileiro',
    naturalized: 'Naturalizado',
    foreigner: 'Estrangeiro',
  })
    .describe('Nacionalidade')
    .nullish(),
  countryOfBirth: z.string().describe('País de nascimento').nullish(),
  isResponsibleFamilyMember: z.boolean().describe('Responsável familiar').nullish(),
  naturalizationDate: z.date().describe('Data de Naturalização').nullish(),
  naturalizationOrdinance: z.string().describe('Portaria de Naturalização').nullish(),
  dateOfEntryInBrazil: z.date().describe('Data de entrada no Brasil').nullish(),
  birthplaceMunicipality: z.string().describe('Município de nascimento').nullish(),
  birthplaceState: z.string().describe('UF/Estado de nascimento').nullish(),
  unknownMother: z.boolean().describe('Mãe desconhecida').nullish(),
  unknownFather: z.boolean().describe('Pai desconhecido').nullish(),
  kinshipWithFamilyResponsible: enumerated({
    spouse: 'Cônjuge',
    sonOrDaughter: 'Filho(a)',
    stepsonOrStepdaughter: 'Enteado(a)',
    grandSonOrGrandDaughterOrGreatGrandsonOrGreatGranddaughter: 'Neto(a)/Bisneto(a)',
    fatherOrMother: 'Pai/Mãe',
    fatherInLawOrMotherInLaw: 'Sogro(a)',
    brotherOrSister: 'Irmão(ã)',
    sonInLawOrDaughterInLaw: 'Genro/Nora',
    otherRelative: 'Outro parente',
    nonRelative: 'Não parente',
  })
    .describe('Relação de parentesco com o responsável familiar')
    .nullish(),
  occupation: z.string().describe('Ocupação').nullish(),
  attendsSchoolOrDaycare: z.boolean().describe('Frequenta escola ou creche').nullish(),
  educationLevel: enumerated({
    nursery: 'Creche',
    preschool: 'Pré-escola',
    literacyClass: 'Classe de alfabetização',
    elementarySchool1StTo4ThGrades: 'Ensino Fundamental I (1º ao 4º ano)',
    elementarySchool5ThTo8ThGrades: 'Ensino Fundamental II (5º ao 8º ano)',
    elementarySchool: 'Ensino Fundamental Completo',
    specialElementarySchool: 'Ensino Fundamental Especial',
    incompleteHigherEducation: 'Ensino Superior Incompleto',
    elementaryEducationForYouthAndAdultsInitialGrades: 'EJA - Ensino Fundamental Inicial',
    elementaryEducationForYouthAndAdultsFinalGrades: 'EJA - Ensino Fundamental Final',
    highSchool: 'Ensino Médio',
    specialHighSchool: 'Ensino Médio Especial',
    highSchoolForYouthAndAdults: 'EJA - Ensino Médio',
    higherEducation: 'Ensino Superior',
    adultLiteracy: 'Alfabetização para Adultos',
    none: 'Nenhum',
  })
    .describe('Grau de instrução/escolaridade')
    .nullish(),
  employmentStatus: enumerated({
    employer: 'Empregador',
    employeeWithFormalEmploymentContract: 'Empregado com contrato formal',
    employeeWithoutFormalEmploymentContract: 'Empregado sem contrato formal',
    selfEmployedWithSocialSecurityCoverage: 'Autônomo com cobertura previdenciária',
    selfEmployedWithoutSocialSecurityCoverage: 'Autônomo sem cobertura previdenciária',
    retiredOrPensioner: 'Aposentado/Pensionista',
    unemployed: 'Desempregado',
    notWorking: 'Não trabalha',
    publicServantOrMilitaryPersonnel: 'Servidor público ou militar',
    other: 'Outro',
  })
    .describe('Situação no mercado de trabalho')
    .nullish(),
  childZeroToNineYearsOld: z.boolean().describe('Criança de 0 a 9 anos').nullish(),
  childZeroToNineYearsOldWhoTakesCare: z
    .string()
    .describe('Cuidador da criança de 0 a 9 anos')
    .nullish(),
  attendsAlternativeHealer: z
    .boolean()
    .describe('Frequenta cuidador tradicional ou popular')
    .nullish(),
  takePartInCommunityGroups: z.boolean().describe('Participa de grupo comunitário').nullish(),
  hasPrivateHealthInsurance: z.boolean().describe('Possui plano de saúde privado').nullish(),
  memberOfTraditionalGroupsOrCommunity: z
    .boolean()
    .describe('Membro de povo ou comunidade tradicional')
    .nullish(),
  wichTraditionalGroupsOrCommunity: z
    .string()
    .describe('Qual povo ou comunidade tradicional')
    .nullish(),
  wishToInformSexualOrientation: z
    .boolean()
    .describe('Deseja informar a orientação sexual')
    .nullish(),
  sexualOrientation: enumerated({
    heterosexual: 'Heterossexual',
    bisexual: 'Bissexual',
    homosexual: 'Homossexual',
    other: 'Outro',
  })
    .describe('Orientação sexual')
    .nullish(),
  wishToInformGenderIdentity: z
    .boolean()
    .describe('Deseja informar a identidade de gênero')
    .nullish(),
  hasAnyDisability: z.boolean().describe('Possui alguma deficiência').nullish(),
  whichDisability: z.string().describe('Quais deficiências possui').nullish(),
  duringTheLastThreeMonthsHadFoodShortageWithoutFinancialMeansToGetMore: z
    .boolean()
    .describe('Falta de alimentos nos últimos três meses sem meios financeiros para mais')
    .nullish(),
  duringTheLastThreeMonthsAteOnlyAvailableFoodBecauseOfLackOfMoney: z
    .boolean()
    .describe('Consumiu apenas alimentos disponíveis nos últimos três meses por falta de dinheiro')
    .nullish(),
  moved: z.boolean().describe('Mudou-se').nullish(),
  isPregnant: z.boolean().describe('Está gestante').nullish(),
  referenceMaternityHospital: z.string().describe('Maternidade de referência').nullish(),
  currentWeightStatus: enumerated({
    underweight: 'Abaixo do peso',
    normalWeight: 'Peso normal',
    overweight: 'Sobrepeso',
  })
    .describe('Situação atual do peso')
    .nullish(),
  smoker: z.boolean().describe('Fumante').nullish(),
  consumesAlcoholicBeverages: z.boolean().describe('Consome bebidas alcoólicas').nullish(),
  takeDrugs: z.boolean().describe('Usa drogas').nullish(),
  hasHypertension: z.boolean().describe('Tem hipertensão arterial').nullish(),
  hasDiabetes: z.boolean().describe('Tem diabetes').nullish(),
  hadStroke: z.boolean().describe('Teve AVC').nullish(),
  hadHeartAttack: z.boolean().describe('Teve infarto').nullish(),
  hasHeartDisease: z.boolean().describe('Possui doença cardíaca').nullish(),
  whichHeartDisease: z.string().describe('Quais doenças cardíacas possui').nullish(),
  hadKidneyDisease: z.boolean().describe('Teve doença renal').nullish(),
  whichKidneyDisease: z.string().describe('Quais doenças renais possui').nullish(),
  hasRespiratoryDisease: z.boolean().describe('Possui doença respiratória').nullish(),
  whichRespiratoryDisease: z.string().describe('Quais doenças respiratórias possui').nullish(),
  hasLeprosy: z.boolean().describe('Está com hanseníase').nullish(),
  hasTuberculosis: z.boolean().describe('Está com tuberculose').nullish(),
  hadCancer: z.boolean().describe('Teve ou está com câncer').nullish(),
  duringTheLast6MonthsHadAnyHospitalInternment: z
    .boolean()
    .describe('Internação nos últimos seis meses')
    .nullish(),
  hospitalInternmentReasons: z.string().describe('Motivo da internação').nullish(),
  hasMedicalProfessionalDiagnosisOfMentalConditions: z
    .boolean()
    .describe('Diagnóstico de saúde mental por profissional')
    .nullish(),
  isBedridden: z.boolean().describe('Está acamado').nullish(),
  isDomiciled: z.boolean().describe('Está domiciliado').nullish(),
  usesMedicinalPlants: z.boolean().describe('Usa plantas medicinais').nullish(),
  whichMedicinalPlants: z.string().describe('Quais plantas medicinais').nullish(),
  makesUseOfOtherIntegrativeAndComplementaryPractices: z
    .boolean()
    .describe('Usa práticas integrativas e complementares')
    .nullish(),
  haveOtherHealthConditions: z.string().describe('Outras condições de saúde').nullish(),
  homeless: z.boolean().describe('Está em situação de rua').nullish(),
  homelessForHowManyTime: enumerated({
    lessThanSixMonths: 'Menos de seis meses',
    sixToTwelveMonths: 'Seis a doze meses',
    oneToFiveYears: 'Um a cinco anos',
    moreThanFiveYears: 'Mais de cinco anos',
  })
    .describe('Tempo em situação de rua')
    .nullish(),
  receivesBenefit: z.boolean().describe('Recebe algum benefício').nullish(),
  haveAnyKindOfFamilyReference: z.boolean().describe('Possui referência familiar').nullish(),
  eatHowManyTimesDay: enumerated({
    once: 'Uma vez',
    twoOrThreeTimes: 'Duas ou três vezes',
    moreThanThreeTimes: 'Mais de três vezes',
  })
    .describe('Quantas vezes se alimenta por dia')
    .nullish(),
  foodSource: z.string().describe('Origem da alimentação').nullish(),
  assistedByAnyInstitution: z.boolean().describe('Acompanhamento de alguma instituição').nullish(),
  assistedByWhichInstitution: z.string().describe('Qual instituição').nullish(),
  visitFamilyMembersOften: z.boolean().describe('Visita familiares com frequência').nullish(),
  kinshipWithVisitedFamilyMember: z
    .string()
    .describe('Parentesco com o familiar visitado')
    .nullish(),
  hasAccessToPersonalHygiene: z.boolean().describe('Acesso à higiene pessoal').nullish(),
  whichAccessToPersonalHygiene: z.string().describe('Quais acessos à higiene pessoal').nullish(),
  refusedToRegister: z.boolean().describe('Recusou-se a cadastrar').nullish(),
})

const { originalId: _, ...citizenShapeWithoutOriginalId } = citizenShapeBase.shape

const shape = z.object({
  originalId: citizenShapeBase.shape['originalId'],
  ...citizenShapeWithoutOriginalId,
  ...citizenLocationShape.shape,
  ...citizenHealthProfileShape.shape,
})

export const citizenHealthProfileExpression = expression(shape.shape)

export const citizenHealthProfileVariable: Variable = {
  key: 'citizenHealthProfile',
  actionDefinitionKey: citizenHealthProfileStatsAction.key,
  title: 'Ficha de cadastro individual (histórico)',
  categoryKey: 'healthIndividualProfile',
  unitSingular: 'ficha de cadastro individual',
  unitPlural: 'fichas de cadastro individual',
  grammaticalGender: 'male',
  timeType: 'incidence',
  shape: getZodSchemaInfo(shape),
  filterableBy: getZodSchemaInfo(
    shape.pick({
      sex: true,
      age: true,
      isResponsibleFamilyMember: true,
      skinColorOrRace: true,
      nationality: true,
      educationLevel: true,
      employmentStatus: true,
      hasPrivateHealthInsurance: true,
      attendsAlternativeHealer: true,
      memberOfTraditionalGroupsOrCommunity: true,
      sexualOrientation: true,
      hasAnyDisability: true,
      isPregnant: true,
      currentWeightStatus: true,
      smoker: true,
      consumesAlcoholicBeverages: true,
      takeDrugs: true,
      hasHypertension: true,
      hasDiabetes: true,
      hadStroke: true,
      hadHeartAttack: true,
      hasHeartDisease: true,
      hadKidneyDisease: true,
      hasRespiratoryDisease: true,
      hasLeprosy: true,
      hasTuberculosis: true,
      hadCancer: true,
      duringTheLast6MonthsHadAnyHospitalInternment: true,
      hasMedicalProfessionalDiagnosisOfMentalConditions: true,
      isBedridden: true,
      isDomiciled: true,
      homeless: true,
      homelessForHowManyTime: true,
      receivesBenefit: true,
      eatHowManyTimesDay: true,
      hasAccessToPersonalHygiene: true,
      unknownFather: true,
      unknownMother: true,
      ethnicity: true,
      attendsSchoolOrDaycare: true,
      takePartInCommunityGroups: true,
      wishToInformSexualOrientation: true,
      wishToInformGenderIdentity: true,
      duringTheLastThreeMonthsAteOnlyAvailableFoodBecauseOfLackOfMoney: true,
      duringTheLastThreeMonthsHadFoodShortageWithoutFinancialMeansToGetMore: true,
      moved: true,
      deceased: true,
      usesMedicinalPlants: true,
      makesUseOfOtherIntegrativeAndComplementaryPractices: true,
      haveAnyKindOfFamilyReference: true,
      foodSource: true,
      assistedByAnyInstitution: true,
      visitFamilyMembersOften: true,
      refusedToRegister: true,
    }),
  ),
  betterDensity: 'more',
  relevance: healthVariableBaseRelevance.citizenHealthProfile,
  basicSelect: ['name', 'socialName'],
  detailedSelect: [
    /* CIDADÃO */
    'sex',
    'birthDate',
    'cns',
    'cpf',
    'phoneNumber',
    'streetName',
    'streetNumber',
    'neighborhood',
    'complement',
    'referencePoint',
    'deceased',
    'postalCode',
    'latitude',
    'longitude',
    'originalId',

    /* SAÚDE */
    'email',
    'mothersName',
    'fathersName',
    'skinColorOrRace',
    'nationality',
    'isResponsibleFamilyMember',
    'countryOfBirth',
    'birthplaceMunicipality',
    'birthplaceState',
    'occupation',
    'wichTraditionalGroupsOrCommunity',
    'hasPrivateHealthInsurance',
    'memberOfTraditionalGroupsOrCommunity',
    'hasAnyDisability',
    'isPregnant',
    'currentWeightStatus',
    'smoker',
    'consumesAlcoholicBeverages',
    'takeDrugs',
    'hasHypertension',
    'hasDiabetes',
    'hadStroke',
    'hadHeartAttack',
    'hasHeartDisease',
    'whichHeartDisease',
    'hadKidneyDisease',
    'whichKidneyDisease',
    'hasRespiratoryDisease',
    'whichRespiratoryDisease',
    'hasLeprosy',
    'hasTuberculosis',
    'hadCancer',
    'hasMedicalProfessionalDiagnosisOfMentalConditions',
    'isBedridden',
    'isDomiciled',
    'homeless',
    'homelessForHowManyTime',
    'receivesBenefit',
  ],
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      ...defaultDetectionLayers,
      {
        layer: 'calendarSeasonality',
        type: 'monthOfYear',
        degree: 1,
        optimization: {
          minimumDays: 1095,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'trend',
        degree: 2,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 1,
        optimization: {
          minimumDays: 60,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'calendarSeasonality',
        type: 'holiday',
        degree: 1,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfMonth',
        degree: 1,
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
    ],
  },
  assertions: prepareAssertions(citizenHealthProfileExpression),
}

const citizenHealthProfileStatsDefinition = defineAction({
  ...citizenHealthProfileStatsAction,
  input: z.object({
    variable,
    highlight: citizenHealthProfileExpression.optional(),
    filter: citizenHealthProfileExpression.optional(),
    space: spaceForPoint().optional(),
    time: timeForIncidence().optional(),
    select: select(shape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (
      input.filter ||
      input.space?.delimitationType === 'custom' ||
      (input.time?.type &&
        ['customDateRange', 'customDateRangePredicted'].includes(input.time?.type))
    ) {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7, // 7 days
        expirationMsForUpdate: 1000 * 60 * 60 * 2, // 2 hours
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7, // 7 days
        expirationMsForUpdate: 1000 * 60 * 60 * 2, // 2 hours
      },
    }
  },
})

export default citizenHealthProfileStatsDefinition
